import { computed, inject, Injectable, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, Subject, takeUntil } from 'rxjs';

import {
    ApiErrorMessage,
    AppShellErrorService,
    AppShellErrorSeverity,
    AppShellErrorStatus,
    ErrorMessage,
} from '@priva/appshell';
import { PrivaNotificationsService } from '@priva/notifications';
import { UniqueIdentifierUtilities } from '@priva/utilities';

@Injectable({ providedIn: 'root' })
export class ErrorHandlerService implements OnDestroy {
    private readonly apiMessages$: Observable<ApiErrorMessage>;
    private readonly scriptMessages$: Observable<ErrorMessage>;
    private readonly destroy$ = new Subject<void>();
    private readonly errorService = inject(AppShellErrorService);
    private readonly notificationsService = inject(PrivaNotificationsService);

    constructor() {
        this.apiMessages$ = this.errorService.apiErrors;
        this.scriptMessages$ = this.errorService.scriptErrors;
    }

    private readonly hasNotificationBar = toSignal(this.notificationsService.barVisibility, {
        initialValue: false,
    });

    readonly notificationBarOffset = computed<number>(() => (this.hasNotificationBar() ? 36 : 0));

    initialize(): void {
        this.apiMessages$
            .pipe(takeUntil(this.destroy$))
            .subscribe((message: ApiErrorMessage) => this.handleApiMessages(message));
        this.scriptMessages$
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => this.handleScriptMessages(message));
    }

    handleApiMessages(error: ApiErrorMessage): void {
        this.processErrorMessage(error);
    }

    handleScriptMessages(message: string | ErrorMessage): void {
        const isStringMessage = typeof message === 'string';

        const scriptErrorMessage: ErrorMessage = {
            uuid: isStringMessage || !message.uuid ? UniqueIdentifierUtilities.createUUID() : message.uuid,
            status: isStringMessage || !message.status ? AppShellErrorStatus.Active : message.status,
            messageLabel: typeof message === 'string' ? message : message.messageLabel,
            severity: AppShellErrorSeverity.Error,
            details: typeof message === 'string' ? '' : message.details,
            source: typeof message === 'string' ? '' : message.source,
        };

        this.processErrorMessage(scriptErrorMessage);
    }

    private processErrorMessage(message: ApiErrorMessage | ErrorMessage): void {
        if (message.status === AppShellErrorStatus.Resolved) {
            this.notificationsService.removeNotification(message.uuid);
            return;
        }

        switch (message.severity) {
            case AppShellErrorSeverity.Error:
                this.notificationsService.bar.error(message.messageLabel, undefined, message.uuid);
                break;
            case AppShellErrorSeverity.Warning:
                this.notificationsService.bar.warning(message.messageLabel, undefined, message.uuid);
                break;
            default:
                break;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
